import React, { PropsWithChildren, useCallback, useMemo } from 'react';
import DossierRoutingContext from './DossierRoutingContext';

export type DossierRoutingProviderProps = {
    orderBasePath: string;
    dossierBasePath: string;
    dossierValidationPath?: string;
    addFormalityPath?: string;
};

const DossierRoutingProvider: React.FunctionComponent<
    PropsWithChildren<DossierRoutingProviderProps>
> = function ({
    orderBasePath,
    dossierBasePath,
    dossierValidationPath,
    addFormalityPath,
    children,
}) {
    const getOrderRoute = useCallback(
        (id: number): string => `/${orderBasePath}/${id}`,
        [orderBasePath],
    );
    const getOrderDuplicateRoute = useCallback(
        (id: number): string => `/${orderBasePath}/${id}/duplication`,
        [orderBasePath],
    );
    const getDossierRoute = useCallback(
        (id: number): string => `/${dossierBasePath}/${id}`,
        [dossierBasePath],
    );
    const getDossiersListeRoute = useCallback(
        (): string => `/${dossierBasePath}`,
        [dossierBasePath],
    );
    const getDossierValidationRoute = useCallback(
        (id: number): string | undefined =>
            dossierValidationPath
                ? `/${dossierBasePath}/${dossierValidationPath.replace(':idDossier', id as unknown as string)}`
                : undefined,
        [dossierBasePath, dossierValidationPath],
    );
    const getAddFormalityRoute = useCallback(
        (id: number): string | undefined =>
            addFormalityPath
                ? `/${dossierBasePath}/${addFormalityPath.replace(':idDossier', id as unknown as string)}`
                : undefined,
        [dossierBasePath, addFormalityPath],
    );

    const contextValue = useMemo(
        () => ({
            getOrderRoute,
            getOrderDuplicateRoute,
            getDossierRoute,
            getDossiersListeRoute,
            getDossierValidationRoute,
            getAddFormalityRoute,
        }),
        [
            getOrderRoute,
            getOrderDuplicateRoute,
            getDossierRoute,
            getDossiersListeRoute,
            getDossierValidationRoute,
            getAddFormalityRoute,
        ],
    );

    return (
        <DossierRoutingContext.Provider value={contextValue}>
            {children}
        </DossierRoutingContext.Provider>
    );
};

export default DossierRoutingProvider;
